import Channel from '@@types/Channel';

import { GNB_TABS, KEYS, TableNames } from '~/constants';
import { isUnreadChannel } from '~/services/channelService';
import { channelsStore } from '~/stores/ChannelsStore';
import { mainDetailUiState } from '~/stores/DetailUiState';
import idb from '~/stores/providers/Idb';
import { uiState } from '~/stores/UiState';
import { userStore } from '~/stores/UserStore';
import { addClass, hasClass, removeClass, scrollIntoViewIfNeeded } from '~/utils/domUtil';
import { isNonEmpty } from '~/utils/fnUtil/fnUtil';
import { lift } from '~/utils/monadUtil';
import { find as _find, findLast as _findLast, isEmpty } from 'lodash';
import {
  __,
  always,
  cond,
  curry,
  equals,
  findIndex,
  head,
  identity,
  ifElse,
  includes,
  last,
  map,
  nth,
  pipe,
  prop,
  propEq,
  tap,
} from 'ramda';
import { Maybe } from 'ramda-fantasy';

import Actions from '../Actions';

const validateNode: (node: Node) => Maybe<Node> = (node) => {
  return isEmpty(node) ? Maybe.Nothing() : Maybe.Just(node);
};

const getOrEdge = curry((index, list) => {
  if (index < 0) {
    return head(list);
  } else if (index >= list.length) {
    return last(list);
  } else {
    return nth(index, list);
  }
});

class GnbActions {
  private findUnreadChannelByDirection = curry((direction: string, index: number): Channel => {
    const { hasUnreadChannel, channels } = channelsStore;
    if (!hasUnreadChannel) return;
    const reverseIndex = direction === 'UP' ? channels.length - 1 : 0;
    const finder = direction === 'UP' ? _findLast : _find;
    return finder(channels, isUnreadChannel, index) || finder(channels, isUnreadChannel, reverseIndex);
  });

  constructor(private actions: Actions) {}

  private get userId() {
    return userStore.id;
  }

  blurGnbListItem = () => {
    pipe<any, any, any>(validateNode, map(removeClass('focus')))(document.querySelector('.js-gnb-list-item.focus'));
  };

  onChannelsOrderChanged = (channels: Channel[]) => {
    const { start, end } = uiState.gnbListRenderRange;
    this.fetchChannelMembers(channels.slice(start, end + 1));
  };

  onKeyDownGnbSearchInput = (key: string) => {
    const { tabs } = userStore;
    cond<[string], void>([
      [equals(KEYS.DOWN), this.focusNextGnbChannel],
      [equals(KEYS.UP), this.focusPrevGnbChannel],
      [equals(KEYS.ENTER), this.clickFocusedGnbListItem],
      [equals(KEYS.LEFT), () => this.selectLeftGnbTab(tabs)],
      [equals(KEYS.RIGHT), () => this.selectRightGnbTab(tabs)],
      [equals(KEYS.ESC), this.actions.mainEditorActions.focus],
      [equals(`${KEYS.ALT}+${KEYS.UP}`), this.selectPrevUnreadChannel],
      [equals(`${KEYS.ALT}+${KEYS.DOWN}`), this.selectNextUnreadChannel],
    ])(key);
  };

  fetchGnbTab = () => {
    idb
      .get(TableNames.Settings, `tabIndex:${this.userId}`)
      .then(ifElse<[string], string, string>(includes(__, userStore.tabs), identity, always(GNB_TABS.FAVORITE)))
      .then(uiState.setGnbTab);
  };

  setGnbTab = (tab: string) => {
    uiState.setGnbTab(tab);
    uiState.clearGnbSearchKeyword();
    return idb.put(TableNames.Settings, `tabIndex:${this.userId}`, tab);
  };

  private fetchChannelMembers = (channels: Channel[]) => {
    this.actions.channelMembersActions.fetchChannelsMembers(channels);
  };

  onChannelsRender = curry(({ startIndex: start, stopIndex: end }, channels) => {
    uiState.setGnbListRenderRange({ start, end });
    this.fetchChannelMembers(channels.slice(start, end + 1));
  });

  private selectLeftGnbTab = (tabs) => {
    if (isNonEmpty(uiState.gnbSearchKeyword)) return;
    const index = findIndex(equals(uiState.gnbTab), tabs);
    this.setGnbTab(tabs[Math.max(index - 1, 0)]);
  };

  private selectRightGnbTab = (tabs) => {
    if (isNonEmpty(uiState.gnbSearchKeyword)) return;
    const index = findIndex(equals(uiState.gnbTab), tabs);
    this.setGnbTab(tabs[Math.min(index + 1, tabs.length - 1)]);
  };

  private focusNextGnbChannel = () => {
    const index = this.findFocusedGnbListItemIndex();
    this.blurGnbListItem();
    this.setFocusGnbChannel(index + 1);
  };

  private focusPrevGnbChannel = () => {
    const index = this.findFocusedGnbListItemIndex();
    this.blurGnbListItem();
    this.setFocusGnbChannel(index - 1);
  };

  private clickFocusedGnbListItem = () => {
    pipe<any, any, any>(
      validateNode,
      map((nodes) => {
        nodes[0].childNodes[0].click();
      })
    )(document.querySelectorAll('.js-gnb-list-item.focus'));
  };

  private selectPrevUnreadChannel = () => {
    this.selectUnreadChannelByDirection('UP');
  };

  private selectNextUnreadChannel = () => {
    this.selectUnreadChannelByDirection('DOWN');
  };

  private findFocusedGnbListItemIndex = () => {
    return pipe<any, any>(findIndex(hasClass('focus')))(document.querySelectorAll('.js-gnb-list-item'));
  };

  private setFocusGnbChannel = (index: number) => {
    pipe<any, any, any, any, any>(
      getOrEdge(index),
      validateNode,
      map(addClass('focus')),
      map(scrollIntoViewIfNeeded)
    )(document.querySelectorAll('.js-gnb-list-item'));
  };

  private selectUnreadChannelByDirection = (direction: string) => {
    pipe<any, any, any, any, any, any, any>(
      findIndex(propEq('channelId', mainDetailUiState.currentChannelId)),
      this.findUnreadChannelByDirection(direction),
      lift(identity),
      map(
        tap((channel: Channel) => {
          window.requestAnimationFrame(() => {
            uiState.setScrollTargetChannel(channel);
          });
        })
      ),
      map(prop('channelId')),
      map(this.actions.channelActions.selectChannel)
    )(channelsStore.channels);
  };
}

export default GnbActions;
